import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import kotVoidReasonService from '../../../../services/kotVoidReasonService';
import { KotVoidReasonDTO, SaveKotVoidReasonDTO } from '../../../../types';
import { ArrowLeft, Plus, Pencil, Trash2, Save, X } from 'lucide-react';
import '../RestaurantPage.css';

interface FormState {
  id: number | null;
  reason: string;
  isActive: boolean;
  sortOrder: number;
}

const blank = (): FormState => ({ id: null, reason: '', isActive: true, sortOrder: 0 });

const KotVoidReasonsPage: React.FC = () => {
  const navigate = useNavigate();

  const [reasons,    setReasons]    = useState<KotVoidReasonDTO[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [deleting,   setDeleting]   = useState<number | null>(null);
  const [error,      setError]      = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [form,       setForm]       = useState<FormState | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await kotVoidReasonService.getAll();
      const d = (res.data as any)?.data ?? [];
      setReasons(Array.isArray(d) ? d : []);
    } catch {
      setError('Failed to load void reasons.');
    } finally {
      setLoading(false);
    }
  };

  const openNew  = () => { setForm(blank()); setError(null); };
  const openEdit = (r: KotVoidReasonDTO) => {
    setForm({ id: r.id, reason: r.reason, isActive: r.isActive, sortOrder: r.sortOrder });
    setError(null);
  };
  const closeForm = () => setForm(null);

  const set = (patch: Partial<FormState>) =>
    setForm(prev => prev ? { ...prev, ...patch } : prev);

  const handleSave = async () => {
    if (!form) return;
    if (!form.reason.trim()) { setError('Reason is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const dto: SaveKotVoidReasonDTO = {
        reason: form.reason.trim(), isActive: form.isActive, sortOrder: form.sortOrder,
      };
      if (form.id === null) {
        const res = await kotVoidReasonService.create(dto);
        const created = (res.data as any)?.data as KotVoidReasonDTO;
        setReasons(prev => [...prev, created]);
        setSuccessMsg('Void reason created.');
      } else {
        const res = await kotVoidReasonService.update(form.id, dto);
        const updated = (res.data as any)?.data as KotVoidReasonDTO;
        setReasons(prev => prev.map(r => r.id === updated.id ? updated : r));
        setSuccessMsg('Void reason updated.');
      }
      setForm(null);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, reason: string) => {
    if (!window.confirm(`Delete "${reason}"?`)) return;
    setDeleting(id);
    try {
      await kotVoidReasonService.delete(id);
      setReasons(prev => prev.filter(r => r.id !== id));
      setSuccessMsg('Void reason deleted.');
    } catch {
      setError('Failed to delete void reason.');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <Layout>
      <div className="rest-list-page">

        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button className="ims-back-btn" onClick={() => navigate('/restaurant/order')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">KOT / BOT Void Reasons</div>
          </div>
          <div className="ims-top-bar-right">
            {!form && (
              <button className="ims-btn ims-btn-primary" onClick={openNew}>
                <Plus size={14} strokeWidth={2} /> Add Reason
              </button>
            )}
          </div>
        </div>

        {successMsg && (
          <div className="ims-alert ims-alert-success" style={{ marginTop: '1rem' }}>
            {successMsg}
            <button className="btn-close" onClick={() => setSuccessMsg(null)} />
          </div>
        )}
        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginTop: '1rem' }}>
            {error}
            <button className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        {form && (
          <div className="ims-card" style={{ marginTop: '1rem' }}>
            <div className="ims-card-header">
              <span className="ims-card-title">
                {form.id === null ? 'New Void Reason' : 'Edit Void Reason'}
              </span>
            </div>
            <div className="ims-card-body">
              <div className="ims-form-row">
                <div className="ims-form-group" style={{ flex: 1 }}>
                  <label className="ims-form-label">
                    Reason <span style={{ color: 'var(--ims-red-text)' }}>*</span>
                  </label>
                  <input
                    className="ims-form-control"
                    value={form.reason}
                    onChange={e => set({ reason: e.target.value })}
                    placeholder="e.g. Wrong item ordered"
                    maxLength={200}
                    autoFocus
                  />
                </div>
                <div className="ims-form-group" style={{ width: 100 }}>
                  <label className="ims-form-label">Sort Order</label>
                  <input
                    className="ims-form-control"
                    type="number"
                    min={0}
                    value={form.sortOrder}
                    onChange={e => set({ sortOrder: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="ims-form-group" style={{ width: 80 }}>
                  <label className="ims-form-label">Active</label>
                  <div style={{ paddingTop: 8 }}>
                    <label className="ims-form-check" style={{ cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        className="ims-check-input"
                        checked={form.isActive}
                        onChange={e => set({ isActive: e.target.checked })}
                      />
                      <span className="ims-check-label">Active</span>
                    </label>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button
                  className="ims-btn ims-btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving
                    ? <><span className="ims-btn-spinner" />Saving…</>
                    : <><Save size={14} strokeWidth={2} />Save</>}
                </button>
                <button
                  className="ims-btn ims-btn-secondary"
                  onClick={closeForm}
                  disabled={saving}
                >
                  <X size={14} strokeWidth={2} /> Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="ims-card" style={{ marginTop: '1rem' }}>
          <div className="ims-card-body" style={{ padding: 0, overflowX: 'auto' }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                <div className="spinner-border spinner-border-sm" role="status" />
              </div>
            ) : reasons.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--ims-text-sub)', fontSize: 13 }}>
                No void reasons yet.{' '}
                <button
                  className="ims-btn ims-btn-primary"
                  style={{ marginLeft: 8, padding: '4px 10px', fontSize: 12 }}
                  onClick={openNew}
                >
                  Add one
                </button>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['Reason', 'Sort Order', 'Status', ''].map(h => (
                      <th key={h} style={{
                        padding: '10px 14px',
                        background: 'var(--ims-bg-th, #f9fafb)',
                        fontWeight: 600, fontSize: 11,
                        color: 'var(--ims-text-sub)',
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                        textAlign: 'left',
                        borderBottom: '1px solid var(--ims-border, #e5e7eb)',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reasons.map(r => (
                    <tr key={r.id} style={{ borderBottom: '1px solid var(--ims-border, #e5e7eb)' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 500 }}>{r.reason}</td>
                      <td style={{ padding: '10px 14px', color: 'var(--ims-text-sub)' }}>{r.sortOrder}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span className={`ims-badge ${r.isActive ? 'badge-active' : 'badge-inactive'}`}>
                          {r.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', display: 'flex', gap: 6 }}>
                        <button
                          className="ims-btn ims-btn-secondary"
                          style={{ padding: '4px 8px' }}
                          onClick={() => openEdit(r)}
                          disabled={!!form}
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          className="btn-delete"
                          style={{ padding: '4px 8px' }}
                          onClick={() => handleDelete(r.id, r.reason)}
                          disabled={deleting === r.id || !!form}
                        >
                          {deleting === r.id
                            ? <span className="spinner-border spinner-border-sm" role="status" />
                            : <Trash2 size={12} />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

      </div>
    </Layout>
  );
};

export default KotVoidReasonsPage;
